// import { VNConfirmModal } from '@/components/VNModal/VNConfirmModal';

import { lazy } from 'react';

import { UserDetailsModal } from '@/components/UserDetailsModal';
import VNAssetEditor from '@/components/VNAssetEditor/VNAssetEditor';
import VNAssetUpload from '@/components/VNAssetUpload/VNAssetUpload';
import ClipUsersModal from '@/modals/components/clip-users-modal/ClipUsersModal';
import CloseCaptionModal from '@/modals/components/close-caption-modal/CloseCaptionModal';
import AnalyticsTooltip from '@/pages/AppAnalytics/AnalyticsTooltip';
import AssetTimeline from '@/pages/assets/AssetTimeline';
import EditMultipleModal from '@/pages/assets/edit-multiple-modal/EditMultipleModal';
import IntakeModal from '@/pages/assets/intake-modal/IntakeModal';
import NotificationHanlderEditor from '@/pages/EventWorkflow/NotificationHanlderEditor';
import NotificationTemplateEditor from '@/pages/EventWorkflow/NotificationTemplateEditor';
import OneBillSnippet from '@/store/forms/Site/OneBillSnippet';

import CategoryModal from './components/CategoryModal';
import ChannelPreviewModal from './components/ChannelPreviewModal';
import CreateOuModal from './components/CreateOuModal';
import DemoModal from './components/DemoModal';
import DeleteFeedItemModal from './components/FeedConfigs/DeleteFeedItemModal';
import FeedConfigModal from './components/FeedConfigs/FeedConfigModal';
// import FeedValidationResultModal from './components/FeedConfigs/FeedValidationResultModal';
import ImportModal from './components/FeedConfigs/ImportModal';
import ShowFeedTaskItemsModal from './components/FeedConfigs/ShowFeedTaskItemsModal';
import ShowTaskModal from './components/FeedConfigs/ShowTaskModal';
import MapperTemplateModal from './components/MapperTemplateModal/MapperTemplateModal';
import SegmentsInReplayModal from './components/SegmentsInReplay';
import { MODAL_KEYS } from './keys';
import type { ModalProps } from './types';
import type { ModalConfig } from './VNModalProvider';

type ModalFactory = (props?: any) => ModalConfig;

const LinkChannel = lazy(() => import('./components/link-channel-modal/LinkChannelModal'));

export const modalRegistry: Record<string, ModalFactory> = {
  [MODAL_KEYS.DEMO]: (props: ModalProps<any>): ModalConfig => ({
    title: 'Demo',
    size: 'sm',
    node: <DemoModal id={props.id} />,
  }),
  [MODAL_KEYS.LINK_CHANNEL]: (props: ModalProps<any>): ModalConfig => ({
    title: 'Link Asset',
    size: 'sm',
    node: <LinkChannel channels={props.channels} />,
  }),
  [MODAL_KEYS.Close_Caption]: (props: ModalProps<any>): ModalConfig => ({
    title: props.title,
    size: 'md',
    cssClassName: props.cssClassName,
    node: <CloseCaptionModal />,
  }),
  [MODAL_KEYS.SHOW_INGEST_STEPS]: (props: ModalProps<any>): ModalConfig => ({
    title: `Ingest Steps: ${props.title}`,
    size: 'md',
    node: <IntakeModal asset={props.asset} />,
  }),
  [MODAL_KEYS.SHOW_CLIP_USES]: (props?: any): ModalConfig => {
    // console.log('props', { props });
    if (!props?.clipUsers) {
      return {
        title: '',
        size: 'lg',
        node: null,
      };
    }

    return {
      title: `${props.entity} Uses: ${props.clipUsers.assetTitle ?? ''}`,
      size: 'lg',
      node: <ClipUsersModal clipUsers={props.clipUsers} />,
    };
  },
  [MODAL_KEYS.ASSET_TIMELINE]: (props: ModalProps<any>): ModalConfig => ({
    title: `Clip Timeline: ${props.title}`,
    size: 'lg',
    node: <AssetTimeline assetAuditTrailsDetails={props.assetAuditTrailsDetails} />,
  }),
  [MODAL_KEYS.EDIT_ASSET]: (props: ModalProps<any>): ModalConfig => ({
    title: 'Edit Asset',
    size: 'xl',
    id: props.id,
    fullscreen: true,
    node: <VNAssetEditor addToList={props.addToList} />,
  }),
  [MODAL_KEYS.UPLOAD_ASSET]: (props: ModalProps<any>): ModalConfig => ({
    title: props.title,
    size: props.size,
    cssClassName: props.cssClassName,
    fullscreen: true,
    node: <VNAssetUpload successCallBack={props.successCallBack} uploadFrom={props.uploadFrom} />,
  }),
  [MODAL_KEYS.EDIT_MULTIPLE_ASSETS]: (props: ModalProps<any>): ModalConfig => ({
    title: props.title,
    size: props.size,
    cssClassName: props.cssClassName,
    fullscreen: true,
    node: <EditMultipleModal onSave={props.onSave} />,
  }),
  [MODAL_KEYS.CHANNEL_PREVIEW]: (props: ModalProps<any>): ModalConfig => ({
    title: props.title,
    size: 'md',
    node: <ChannelPreviewModal channel={props.channel} videoJsOptions={props.videoJsOptions} />,
  }),
  [MODAL_KEYS.SEGMENTS_IN_REPLAY]: (props: ModalProps<any>): ModalConfig => ({
    title: 'Edit Nonstop Live Channel Segments in Replay',
    size: 'lg',
    node: <SegmentsInReplayModal serviceId={props.serviceId} />,
  }),
  [MODAL_KEYS.CREATE_OU]: (props: ModalProps<any>): ModalConfig => ({
    title: 'Create New Ou',
    size: 'sm',
    node: <CreateOuModal form={props.form} onSave={props.onSave} />,
  }),
  [MODAL_KEYS.OU_CATEGORY]: (props: ModalProps<any>): ModalConfig => ({
    title: props.title,
    size: 'sm',
    node: <CategoryModal categoryForm={props.categoryForm} onSaveClick={props.onSaveClick} />,
  }),
  [MODAL_KEYS.FEED_CONFIG]: (props: ModalProps<any>): ModalConfig => ({
    title: props.title,
    size: 'sm',
    node: <FeedConfigModal form={props.form} onSave={props.onSave} isExisting={props.isExisting} />,
  }),
  // [MODAL_KEYS.FEED_VALIDATION_RESULT]: (props: ModalProps<any>): ModalConfig => ({
  //   title: props.title,
  //   size: 'lg',
  //   node: <FeedValidationResultModal form={props.form} inputData={props.inputData} sourceXML={props.sourceXML} />,
  // }),
  [MODAL_KEYS.IMPORT_CSV_FEED]: (props: ModalProps<any>): ModalConfig => ({
    title: props.title,
    size: 'sm',
    node: <ImportModal templates={props.templates} onUpload={props.onUpload} />,
  }),
  [MODAL_KEYS.SHOW_FEED_TASK]: (props: ModalProps<any>): ModalConfig => ({
    title: 'Tasks',
    size: 'lg',
    node: <ShowTaskModal ouId={props.ouId} />,
  }),
  [MODAL_KEYS.SHOW_FEED_ITEM_TASK]: (props: ModalProps<any>): ModalConfig => ({
    title: 'Tasks',
    size: 'lg',
    node: <ShowFeedTaskItemsModal items={props.items} pagination={props.pagination} />,
  }),
  [MODAL_KEYS.DELETE_FEED_ITEM]: (props: ModalProps<any>): ModalConfig => ({
    title: 'Feed Item',
    size: 'sm',
    node: <DeleteFeedItemModal onDelete={props.onFeedItemDelete} />,
  }),
  [MODAL_KEYS.MRSS_TEMPLATE]: (props: ModalProps<any>): ModalConfig => ({
    title: props.isExisting ? 'Update Template' : 'Create Template',
    size: 'lg',
    node: (
      <MapperTemplateModal
        templateSamples={props.templateSamples}
        form={props.form}
        onSave={props.onSave}
        isExisting={props.isExisting}
        onTestMapper={props.onTestMapper}
      />
    ),
  }),
  [MODAL_KEYS.EVENT_HANDLER]: (props: ModalProps<any>): ModalConfig => ({
    title: props.title,
    size: 'lg',
    node: <NotificationHanlderEditor id={props.id} addToList={props.addToList} workflow={props.workflow} />,
  }),
  [MODAL_KEYS.EVENT_TEMPLATE]: (props: ModalProps<any>): ModalConfig => ({
    title: props.title,
    size: 'lg',
    node: <NotificationTemplateEditor id={props.id} addToList={props.addToList} workflow={props.workflow} />,
  }),
  [MODAL_KEYS.APP_ANALYTICS_TOOLTIP]: (props: ModalProps<any>): ModalConfig => ({
    title: props.title ?? 'Analytics Explanation',
    size: 'sm',
    node: <AnalyticsTooltip tooltipItems={props.tooltipItems} chartKey={props.chartKey} />,
  }),
  [MODAL_KEYS.USER_CREATION]: (props: ModalProps<any>): ModalConfig => ({
    title: props.title,
    size: 'sm',
    node: (
      <UserDetailsModal
        isExisting={props.isExisting}
        userId={props.userId}
        isSuperAdmin={props.isSuperAdmin}
        handleClose={props.handleClose}
      />
    ),
  }),
  [MODAL_KEYS.ONE_BILL_SCRIPT_SNIPPET]: (props: ModalProps<any>): ModalConfig => ({
    title: 'Onebill Snippet',
    size: 'md',
    node: <OneBillSnippet cssUrl={props.cssUrl} logoUrl={props.logoUrl} />,
  }),
};
